import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles, Grid } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { IdPropType } from '../../../proptypes/basic';
import Button from '../../buttons/components/Button';
import { EMAIL_DRAFT_RESOURCE } from '../../api/resources';
import { destroyFulfilled, useEmailDraft } from '../emailDraftsSlice';
import { useDialog } from '../../dialogs/components/DialogContext';
import DeleteButton from 'Modules/buttons/components/DeleteButton';
import { deleteEmailDraft } from 'Modules/mails/utils';
import {
    NURSE_REQUEST_ENTITY,
    NURSE_REQUEST_CANCELATION_ENTITY,
    NURSE_SUGGESTION_ENTITY,
    INFOMATERIAL_ENTITY,
} from '../../mailTemplates/entities';
import { useNurseRequestSend, useNurseRequestCancelationSend } from '../../nurseRequests/hooks';
import { updateCustomer } from '../../customers/customersSlice';
import PropTypes from 'prop-types';
import { withPathDerivedName } from '../../files/utils';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
    },

    marginRight: {
        marginRight: theme.spacing(2),
    },
    inputBase: {
        '& .MuiInputBase-input': {
            padding: '0px',
            paddingRight: '5px',
            paddingLeft: '5px',
            paddingTop: '5px',
        },
    },
}));

const draftReceiverToValue = (receiver) => {
    const emailAddress = receiver.emailAddress || receiver.email_address;
    if (emailAddress?.address) {
        return emailAddress.name
            ? `${emailAddress.name} <${emailAddress.address}>`
            : emailAddress.address;
    }

    return receiver.email_address_value || receiver.emailAddressValue || receiver.value || null;
};

const draftReceiversByType = (draft, type) =>
    (draft.emailReceivers || [])
        .filter((receiver) => receiver.type === type)
        .map(draftReceiverToValue)
        .filter((receiver) => !!receiver);

const prepareMailDraftParameters = (draft) => {
    return {
        parentId: draft.id,
        customerId: draft.customer_id,
        receivers: draftReceiversByType(draft, 'to'),
        cc: draftReceiversByType(draft, 'cc'),
        attachments: draft?.attachments?.map(withPathDerivedName).map((a) => ({
            filename: a.name,
            path: a.path,
        })),
        subject: `${draft.subject}`,
        text: `${draft.body.replaceAll('\n', '<br> ')}`,
    };
};

const MailDraftActions = ({ draftId, onDeleted, onSent, onCloseWithoutSent }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [draft] = useEmailDraft(draftId);
    const { openSendMailDialog } = useDialog();

    const handleDraftSent = useCallback(
        (...args) => {
            if (draft) {
                // Sending with a draft ID deletes the draft on the backend.
                // Mirror that deletion locally so a stale final list item is not
                // selected again while the refreshed list is loading.
                dispatch(destroyFulfilled(draft.id));
            }
            if (onSent) {
                onSent(...args);
            }
        },
        [dispatch, draft, onSent]
    );

    const handleInfomaterialDraftSent = useCallback(
        async (...args) => {
            const customerId = draft?.customer_id;
            if (customerId) {
                await dispatch(
                    updateCustomer({
                        id: customerId,
                        infomaterialSentAt: new Date(),
                        infomaterialSentVia: ['email'],
                    })
                );
            }
            handleDraftSent(...args);
        },
        [dispatch, draft, handleDraftSent]
    );

    // A draft only remembers which specialized flow created it (entityName) and
    // that flow's extra form values (extraFields, e.g. careNeedId) - see
    // SendMailForm's draft creation. To keep sending a continued draft doing
    // the same follow-up actions as sending it fresh (e.g. storeNurseRequest
    // per selected agency), that flow's onSent/extraEntities has to be rebuilt
    // here from those two fields instead of being lost.
    const {
        prepareExtraEntities: prepareNurseRequestEntities,
        handleSent: nurseRequestHandleSent,
    } = useNurseRequestSend(draft?.customer_id, draft?.extraFields?.careNeedId);

    const handleNurseRequestDraftSent = useCallback(
        async (args) => {
            const results = await nurseRequestHandleSent(args);
            handleDraftSent(results);
        },
        [nurseRequestHandleSent, handleDraftSent]
    );

    const prepareNurseRequestExtraEntities = useCallback(
        () =>
            Promise.resolve(prepareNurseRequestEntities()).then((entities) => ({
                ...entities,
                careNeedId: draft?.extraFields?.careNeedId ?? null,
            })),
        [prepareNurseRequestEntities, draft]
    );

    const {
        prepareExtraEntities: prepareCancelationExtraEntities,
        handleSent: cancelationHandleSent,
    } = useNurseRequestCancelationSend(draft?.customer_id, draft?.extraFields?.careNeedId);

    const handleCancelationDraftSent = useCallback(
        async (args) => {
            const results = await cancelationHandleSent(args);
            handleDraftSent(results);
        },
        [cancelationHandleSent, handleDraftSent]
    );

    const handleContinue = useCallback(() => {
        let extraEntities = null;
        let handleSent = handleDraftSent;

        if (draft.entityName === NURSE_REQUEST_ENTITY) {
            extraEntities = prepareNurseRequestExtraEntities;
            handleSent = handleNurseRequestDraftSent;
        } else if (draft.entityName === NURSE_REQUEST_CANCELATION_ENTITY) {
            extraEntities = prepareCancelationExtraEntities;
            handleSent = handleCancelationDraftSent;
        } else if (draft.entityName === INFOMATERIAL_ENTITY) {
            handleSent = handleInfomaterialDraftSent;
        } else if (draft.entityName === NURSE_SUGGESTION_ENTITY && draft.extraFields?.sendParams) {
            // Without nurse_suggestion_id the backend can't mark the
            // suggestion as presented - see SendNurseSuggestionButton.
            const { sendParams } = draft.extraFields;
            extraEntities = () => ({ sendParams });
        }

        openSendMailDialog({
            entity: draft.entityName || 'Customer',
            entityId: draft.customer_id,
            templateId: draft.email_template_id,
            draftId: draft.id,
            extraEntities,
            onSent: handleSent,
            onCloseWithoutSent: onCloseWithoutSent,
            prefill: prepareMailDraftParameters(draft),
        });
    }, [
        openSendMailDialog,
        draft,
        prepareNurseRequestExtraEntities,
        handleNurseRequestDraftSent,
        prepareCancelationExtraEntities,
        handleCancelationDraftSent,
        handleInfomaterialDraftSent,
        handleDraftSent,
        onCloseWithoutSent,
    ]);

    const handleDeleteDraft = useCallback(() => {
        if (!draft) {
            return undefined;
        }

        return deleteEmailDraft(draft.id).then((response) => {
            // The custom draft endpoint bypasses the resource actions.
            // Update the resource cache as well so list sagas remove the
            // deleted ID and cannot immediately select the stale draft.
            dispatch(destroyFulfilled(draft.id));
            if (onDeleted) {
                onDeleted();
            }
            return response;
        });
    }, [dispatch, draft, onDeleted]);

    return (
        <Grid container justify="space-between" className={classes.root}>
            <Grid item>
                <Button
                    disabled={!draft}
                    subject={draft || EMAIL_DRAFT_RESOURCE}
                    className={classes.marginRight}
                    color="primary"
                    variant="contained"
                    onClick={handleContinue}
                    startIcon={<EditIcon />}
                >
                    Bearbeiten & Versenden
                </Button>

                {draft && (
                    <DeleteButton
                        buttonLabel="Entwurf löschen!"
                        confirmLabel="Diesen Email-Entwurf löschen?"
                        onClick={handleDeleteDraft}
                    />
                )}
            </Grid>
        </Grid>
    );
};

MailDraftActions.propTypes = {
    draftId: IdPropType,
    onDeleted: PropTypes.func,
    onSent: PropTypes.func,
    onCloseWithoutSent: PropTypes.func,
};

MailDraftActions.defaultProps = {
    draftId: null,
    onDeleted: null,
    onSent: null,
    onCloseWithoutSent: null,
};

export default MailDraftActions;
